import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  acceptVoiceAgreement,
  buyVoiceNumber,
  getVoiceSetup,
  provisionVoiceCalling,
  releaseVoiceNumber,
  requestVoiceApproval,
  saveVoiceBusinessDetails,
  setDefaultVoiceNumber,
} from "../api/voiceApi";
import { showErrorFromMutation, showSuccess } from "@/shared/utils/toast";

export const VOICE_SETUP_QUERY_KEY = ["voice-setup"];

export function useVoiceSetup(options = {}) {
  return useQuery({
    queryKey: VOICE_SETUP_QUERY_KEY,
    queryFn: getVoiceSetup,
    ...options,
  });
}

/** Every setup endpoint returns the fresh setup payload, so the cache is replaced directly. */
function useSetupMutation(mutationFn, { success, failure }) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (setup) => {
      queryClient.setQueryData(VOICE_SETUP_QUERY_KEY, setup);
      if (success) showSuccess(success);
    },
    onError: (error) => showErrorFromMutation(error, failure),
  });
}

export const useRequestVoiceApproval = () =>
  useSetupMutation(requestVoiceApproval, {
    success: "Request sent to SalesWorx for approval.",
    failure: "Could not send the request.",
  });

export const useProvisionVoiceCalling = () =>
  useSetupMutation(provisionVoiceCalling, {
    success: "Voice calling is set up.",
    failure: "Could not set up voice calling.",
  });

export const useSaveVoiceBusinessDetails = () =>
  useSetupMutation(saveVoiceBusinessDetails, {
    success: "Business details saved.",
    failure: "Could not save business details.",
  });

export const useAcceptVoiceAgreement = () =>
  useSetupMutation(acceptVoiceAgreement, {
    success: "Agreement accepted.",
    failure: "Could not record the agreement.",
  });

export const useBuyVoiceNumber = () =>
  useSetupMutation(buyVoiceNumber, {
    success: "Phone number purchased.",
    failure: "Could not buy this number.",
  });

export const useSetDefaultVoiceNumber = () =>
  useSetupMutation(setDefaultVoiceNumber, {
    success: "Default number updated.",
    failure: "Could not change the default number.",
  });

export const useReleaseVoiceNumber = () =>
  useSetupMutation(releaseVoiceNumber, {
    success: "Phone number released.",
    failure: "Could not release this number.",
  });
